import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import {
  HistoryOutlined as HistoryOutlinedIcon,
  MonitorOutlined as MonitorOutlinedIcon,
  NotificationsNone as NotificationsNoneIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import StatCard from '../../Components/GatewayContagem/StatCard';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import DateRangePopover from '../../Components/GatewayContagem/DateRangePopover';
import DevicesTable from '../../Components/GatewayContagem/DevicesTable';
import EventsTable from '../../Components/GatewayContagem/EventsTable';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import { eventAPI } from '../../API/eventAPI';
import { deviceAPI } from '../../API/deviceAPI';
import { storeAPI } from '../../API/storeAPI';
import { buildQueryString } from '../../Utils/format';
import { expandSeverityParam } from '../../Utils/eventHelpers';

const BADGE_COLORS = [
  '#ea580c',
  '#7c3aed',
  '#0d9488',
  '#059669',
  '#d97706',
  '#1B75BB',
  '#db2777',
  '#2563eb',
];

const mapToTableDevice = (device, clientMap, storeMap) => ({
  id: device.id,
  deviceId: device.id,
  name: device.name || device.serialNumber || String(device.id),
  serialNumber: device.serialNumber || null,
  storeId: device.storeId,
  clientId: device.clientId,
  zone: device.location || null,
  storeName: storeMap[device.storeId] ?? '—',
  clientName: clientMap[device.clientId]?.name ?? '—',
  status: device.status,
  lastSeen: device.lastHeartbeat,
  firmware: device.firmwareVersion || null,
});

const PERIODS = ['today', '7d', '30d', 'month', 'custom'];

const computeDatesForPeriod = (period) => {
  const end = new Date();
  const start = new Date();

  if (period === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (period === '7d') {
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (period === '30d') {
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (period === 'month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  } else if (period === 'custom') {
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  }

  return { start, end };
};

const TabPanel = ({ children, value, index }) => (
  value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
);

const EventsMonitorPage = () => {
  const { t } = useTranslation(['events', 'dashboard', 'devices']);
  const theme = useTheme();

  const periodLabel = (value) => {
    const map = { today: 'today', '7d': 'sevenDays', '30d': 'thirtyDays', month: 'month', custom: 'custom' };
    return t(`dashboard:filters.${map[value] || value}`);
  };

  const {
    selectedClient,
    loadClients,
    clients,
  } = useGatewayContagem();

  const initialDates = computeDatesForPeriod('7d');

  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [isLoading, setLoading] = useState(true);

  const [eventsPage, setEventsPage] = useState(0);
  const [eventsItemsPerPage, setEventsItemsPerPage] = useState(10);

  const [eventsSearch, setEventsSearch] = useState('');
  const [debouncedEventsSearch, setDebouncedEventsSearch] = useState('');
  const [eventsSeverityLevel, setEventsSeverityLevel] = useState([]);
  const [eventsStatusFilter, setEventsStatusFilter] = useState(['open']);

  const [period, setPeriod] = useState('7d');
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);

  const [tab, setTab] = useState(0);

  const [rawDevices, setRawDevices] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [storeMap, setStoreMap] = useState({});
  const [devicesLoading, setDevicesLoading] = useState(true);

  const [stats, setStats] = useState({
    criticalOpen: 0,
    warningsActive: 0,
    notificationsSent: 0,
    retentionDays: null,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!clients.length) {
      loadClients();
    }
  }, [clients.length, loadClients]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedEventsSearch(eventsSearch);
    }, 300);

    return () => clearTimeout(timeout);
  }, [eventsSearch]);

  useEffect(() => {
    const { start, end } = computeDatesForPeriod(period);
    setStartDate(start);
    setEndDate(end);
    setEventsPage(0);
  }, [period]);

  useEffect(() => {
    setEventsPage(0);
  }, [
    selectedClient?.id,
    debouncedEventsSearch,
    eventsSeverityLevel,
    eventsStatusFilter,
  ]);

  useEffect(() => {
    let active = true;

    const loadDevicesAndLookups = async () => {
      setDevicesLoading(true);

      try {
        const [devicesSettled, storesSettled] = await Promise.allSettled([
          deviceAPI.list(buildQueryString({ page: 1, itemsPerPage: 500 })),
          storeAPI.list({ page: 1, itemsPerPage: 500 }),
        ]);

        if (!active) return;

        const clientsMap = {};
        (clients || []).forEach((client, index) => {
          if (client.id) {
            const entry = {
              name: client.name,
              color: BADGE_COLORS[index % BADGE_COLORS.length],
            };
            clientsMap[client.id] = entry;
            clientsMap[String(client.id).toLowerCase()] = entry;
          }
        });

        const storesMap = {};
        if (storesSettled.status === 'fulfilled' && storesSettled.value?.value?.items) {
          storesSettled.value.value.items.forEach((store) => {
            if (store.id) {
              storesMap[store.id] = store.name;
              storesMap[String(store.id).toLowerCase()] = store.name;
            }
          });
        }

        const rawDevs = devicesSettled.status === 'fulfilled' && Array.isArray(devicesSettled.value?.value?.items)
          ? devicesSettled.value.value.items
          : [];

        setClientMap(clientsMap);
        setStoreMap(storesMap);
        setRawDevices(rawDevs);
      } catch {
        if (active) {
          toast.error(t('devices:feedback.errorLoadingDevices', 'Erro ao carregar dispositivos'));
        }
      } finally {
        if (active) {
          setDevicesLoading(false);
        }
      }
    };

    loadDevicesAndLookups();

    return () => {
      active = false;
    };
  }, [clients, t]);

  const loadEvents = useCallback(async () => {
    if (!selectedClient?.id) {
      return;
    }

    setLoading(true);

    const hasOpen = eventsStatusFilter.includes('open');
    const hasResolved = eventsStatusFilter.includes('resolved');
    const isResolved = (hasOpen && hasResolved) || (!hasOpen && !hasResolved)
      ? undefined
      : hasResolved;

    const severityLevelParam = expandSeverityParam(eventsSeverityLevel);

    const finalEndDate = endDate ? new Date(endDate) : null;
    if (finalEndDate) {
      finalEndDate.setHours(23, 59, 59, 999);
    }

    const result = await eventAPI.list({
      page: eventsPage + 1,
      itemsPerPage: eventsItemsPerPage,
      clientId: selectedClient?.id,
      search: debouncedEventsSearch,
      severityLevel: severityLevelParam,
      isResolved,
      startDate,
      endDate: finalEndDate,
      sortBy: 'createdAt',
      sortDirection: 'desc',
    });

    setLoading(false);

    if (!result || result.error) {
      setEvents([]);
      setEventsTotal(0);
      toast.error(t('feedback.errorLoadingEvents'));
      return;
    }

    const paginatedResult = result.value;

    setEvents(
      Array.isArray(paginatedResult?.items)
        ? paginatedResult.items
        : [],
    );

    setEventsTotal(paginatedResult?.totalCount ?? 0);
  }, [
    selectedClient?.id,
    eventsPage,
    eventsItemsPerPage,
    debouncedEventsSearch,
    eventsSeverityLevel,
    eventsStatusFilter,
    startDate,
    endDate,
    t,
  ]);

  const loadStats = useCallback(async () => {
    if (!selectedClient?.id) return;
    setStatsLoading(true);

    const finalEndDate = endDate ? new Date(endDate) : null;
    if (finalEndDate) {
      finalEndDate.setHours(23, 59, 59, 999);
    }

    try {
      const result = await eventAPI.getStats({
        clientId: selectedClient.id,
        startDate,
        endDate: finalEndDate,
      });
      if (result && !result.error && result.value) {
        setStats(result.value);
      }
    } catch {
      // Silently catch stats error
    } finally {
      setStatsLoading(false);
    }
  }, [selectedClient?.id, startDate, endDate]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSaveComment = async (eventId, note) => {
    const result = await eventAPI.saveComment(eventId, note);
    if (result && !result.error) {
      toast.success(t('feedback.commentSaved', 'Comentário gravado com sucesso'));
      loadEvents();
    } else {
      toast.error(t('feedback.commentError', 'Erro ao gravar comentário'));
    }
  };

  const handleResolve = async (eventId) => {
    const result = await eventAPI.resolve(eventId);
    if (result && !result.error) {
      toast.success(t('feedback.eventResolved'));
      loadEvents();
      loadStats();
      window.dispatchEvent(new CustomEvent('gateway-events-updated'));
    } else {
      toast.error(t('feedback.resolveError'));
    }
  };

  const handleMarkAllRead = async () => {
    const result = await eventAPI.resolveAll({ clientId: selectedClient?.id });
    if (result && !result.error) {
      toast.success(t('feedback.allEventsResolved'));
      loadEvents();
      loadStats();
      window.dispatchEvent(new CustomEvent('gateway-events-updated'));
    } else {
      toast.error(t('feedback.resolveError'));
    }
  };

  const deviceScope = useMemo(
    () => (
      selectedClient?.id
        ? rawDevices.filter((device) => device.clientId === selectedClient.id)
        : rawDevices
    ),
    [rawDevices, selectedClient],
  );

  const deviceStats = useMemo(() => {
    const total = deviceScope.length;
    const online = deviceScope.filter((device) => device.status === 'ONLINE').length;

    return { total, online };
  }, [deviceScope]);

  const tableDevices = useMemo(
    () => deviceScope.map((device) => mapToTableDevice(device, clientMap, storeMap)),
    [deviceScope, clientMap, storeMap],
  );

  const enrichedEvents = useMemo(() => {
    return events.map((event) => {
      const eventDeviceId = event.deviceId ? String(event.deviceId).toLowerCase() : null;
      const device = rawDevices.find((d) => (
        (d.id && String(d.id).toLowerCase() === eventDeviceId) ||
        (d.serialNumber && String(d.serialNumber).toLowerCase() === eventDeviceId) ||
        (d.name && event.deviceName && d.name.toLowerCase() === event.deviceName.toLowerCase())
      ));

      const effectiveClientId = event.clientId || device?.clientId || selectedClient?.id || null;
      const client = clients.find((c) => effectiveClientId && String(c.id).toLowerCase() === String(effectiveClientId).toLowerCase());
      const clientName = client?.name ||
        (effectiveClientId ? (clientMap[effectiveClientId]?.name || clientMap[String(effectiveClientId).toLowerCase()]?.name) : null) ||
        event.clientName ||
        event.brandName ||
        selectedClient?.name ||
        '—';

      const effectiveStoreId = event.storeId || null;
      const resolvedStoreName = effectiveStoreId
        ? (storeMap[effectiveStoreId] || storeMap[String(effectiveStoreId).toLowerCase()])
        : null;

      const storeName = resolvedStoreName ||
        event.storeName ||
        '—';

      return {
        ...event,
        deviceId: device?.id || event.deviceId,
        deviceName: device?.name || device?.serialNumber || event.deviceName || event.deviceId,
        clientId: effectiveClientId,
        clientName,
        storeId: effectiveStoreId,
        storeName,
        brandName: clientName,
      };
    });
  }, [events, rawDevices, clients, clientMap, storeMap, selectedClient]);

  const visibleCriticalOpen = stats.criticalOpen;

  const visibleWarningsActive = stats.warningsActive;

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 64,
            left: 0,
            width: '100%',
            zIndex: 10,
          }}
        />
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      >
        {statsLoading ? (
          <Skeleton
            variant="rounded"
            width={125}
            height={28}
            sx={{ borderRadius: '6px' }}
          />
        ) : (
          <Tooltip
            title={
              stats.retentionDays && stats.retentionDays > 0
                ? t('retention.tooltipDays', { count: stats.retentionDays })
                : t('retention.tooltipIndefinite')
            }
            arrow
          >
            <Chip
              icon={<HistoryOutlinedIcon sx={{ fontSize: '16px !important' }} />}
              label={
                stats.retentionDays && stats.retentionDays > 0
                  ? t('retention.days', { count: stats.retentionDays })
                  : t('retention.indefinite')
              }
              size="small"
              variant="outlined"
              sx={{
                height: '28px',
                borderRadius: '6px',
                borderColor: '#e5e7eb',
                bgcolor: '#f9fafb',
                color: '#4b5563',
                fontSize: '12.5px',
                fontWeight: 500,
                cursor: 'default',
                '& .MuiChip-icon': {
                  color: '#6b7280',
                  ml: '8px',
                },
                '&:hover': {
                  bgcolor: '#f3f4f6',
                  borderColor: '#d1d5db',
                },
              }}
            />
          </Tooltip>
        )}
      </PageHeader>

      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <StatCard
            title={t('kpi.criticalOpen')}
            value={visibleCriticalOpen}
            subtitle={t('kpi.criticalSub')}
            icon={<WarningAmberIcon fontSize="inherit" />}
            accentColor={theme.palette.error.main}
            loading={isLoading}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <StatCard
            title={t('kpi.warningsActive')}
            value={visibleWarningsActive}
            subtitle={t('kpi.warningsSub')}
            icon={<WarningAmberIcon fontSize="inherit" />}
            accentColor={theme.palette.warning.main}
            loading={isLoading}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <StatCard
            title={t('kpi.devicesOnline')}
            value={
              <>
                {deviceStats.online}

                <Typography
                  component="span"
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: '#c0c4cc',
                    ml: '4px',
                  }}
                >
                  /{deviceStats.total}
                </Typography>
              </>
            }
            subtitle={
              !devicesLoading && deviceStats.total > 0
                ? t('kpi.devicesSla', {
                  value: ((deviceStats.online / deviceStats.total) * 100).toFixed(1),
                })
                : undefined
            }
            icon={<MonitorOutlinedIcon fontSize="inherit" />}
            loading={devicesLoading}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <StatCard
            title={t('kpi.notificationsSent')}
            value={stats.notificationsSent}
            subtitle={t('kpi.notificationsSub')}
            icon={<NotificationsNoneIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{
            minHeight: 'unset',
            display: 'inline-flex',
            bgcolor: '#f4f5f7',
            border: '1px solid #e6e7ea',
            borderRadius: '10px',
            p: '4px',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-flexContainer': {
              gap: '6px',
            },
            '& .MuiTabs-scroller': {
              overflow: 'visible !important',
            },
          }}
        >
          {[
            { label: t('tabs.events') },
            { label: t('tabs.devices') },
          ].map(({ label }) => (
            <Tab
              key={label}
              label={label}
              sx={{
                minHeight: 'unset',
                textTransform: 'none',
                fontSize: '13.5px',
                fontWeight: 400,
                px: '12px',
                py: '6px',
                borderRadius: '7px',
                border: '1px solid transparent',
                color: '#6b7280',
                bgcolor: 'transparent',
                transition: 'color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                '&.Mui-selected': {
                  bgcolor: '#ffffff',
                  color: '#1f2937',
                  fontWeight: 500,
                  border: '1px solid #e6e7ea',
                  boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
                },
                '&:not(.Mui-selected):hover': {
                  color: '#374151',
                  bgcolor: 'rgba(0,0,0,0.02)',
                },
              }}
            />
          ))}
        </Tabs>

        {tab === 0 && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
          >
            <ToggleButtonGroup
              size="small"
              value={period}
              exclusive
              onChange={(_, value) => {
                if (value !== null) setPeriod(value);
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    bgcolor: 'primary.dark',
                    color: '#fff',
                    borderColor: 'primary.dark',
                    '&:hover': { bgcolor: 'primary.main' },
                  },
                },
              }}
            >
              {PERIODS.map((currentPeriod) => (
                <ToggleButton
                  key={currentPeriod}
                  value={currentPeriod}
                >
                  {periodLabel(currentPeriod)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <DateRangePopover
              startDate={startDate}
              endDate={endDate}
              onStartChange={(date) => {
                if (!date) return;
                setPeriod(null);
                setStartDate(date);
                setEventsPage(0);
              }}
              onEndChange={(date) => {
                if (!date) return;
                setPeriod(null);
                setEndDate(date);
                setEventsPage(0);
              }}
              startLabel={t('dashboard:filters.dateStart')}
              endLabel={t('dashboard:filters.dateEnd')}
            />
          </Stack>
        )}
      </Box>

      <TabPanel
        value={tab}
        index={0}
      >
        <EventsTable
          events={enrichedEvents}
          totalCount={eventsTotal}
          page={eventsPage}
          rowsPerPage={eventsItemsPerPage}
          search={eventsSearch}
          severityLevel={eventsSeverityLevel}
          statusFilter={eventsStatusFilter}
          loading={isLoading}
          onPageChange={(_, newPage) => setEventsPage(newPage)}
          onRowsPerPageChange={(event) => {
            setEventsItemsPerPage(Number(event.target.value));
            setEventsPage(0);
          }}
          onSearchChange={(value) => {
            setEventsSearch(value);
            setEventsPage(0);
          }}
          onSeverityLevelChange={(levels) => {
            setEventsSeverityLevel(levels);
            setEventsPage(0);
          }}
          onStatusFilterChange={(status) => {
            setEventsStatusFilter(status);
            setEventsPage(0);
          }}
          onSaveComment={handleSaveComment}
          onResolve={handleResolve}
          onMarkAllRead={handleMarkAllRead}
        />
      </TabPanel>

      <TabPanel
        value={tab}
        index={1}
      >
        <DevicesTable devices={tableDevices} />
      </TabPanel>
    </Box>
  );
};

export default EventsMonitorPage;
